import { z } from 'zod';
import { CLASS_COLORS } from '@repo/shared-types';
import { teachingRoleSchema } from './payroll';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'startTime phải dạng HH:mm');

/** One row of a slot's teacher list. `rateOverride` is pay data: the route
 *  IGNORES it (carrying the stored value forward) for a caller without
 *  `manage:members`, rather than rejecting — the same field-level drop
 *  serializeSlotDetail does on the way out. Capped at 10 rows: a session
 *  with eleven teachers is a data-entry accident, not a class. */
export const slotTeacherInputSchema = z.object({
  membershipId: z.string().min(1),
  role: teachingRoleSchema,
  rateOverride: z.number().int().min(0).max(100_000_000).nullable().optional(),
  // Independent of the teacher's own unit; only meaningful alongside a set
  // rateOverride. Null/omitted = fall back to the teacher's own rateUnit.
  rateOverrideUnit: z.enum(['PER_SESSION', 'PER_HOUR']).nullable().optional(),
});

/** Same list for one session — no rate of any kind (no session-level
 *  override, by design; a one-off price exception is an Điều chỉnh). */
export const sessionTeacherInputSchema = z.object({
  membershipId: z.string().min(1),
  role: teachingRoleSchema,
});

export const createSlotSchema = z.object({
  teacherId: z.string().min(1).optional(), // defaults to caller's membership server-side
  subjectId: z.string().min(1),
  // THI-267: the ClassGroup (lớp) this khung giờ belongs to. Absent → the
  // route creates an implicit one-slot group with a derived name (spec §4) —
  // every ClassSlot has a groupId at the DB level, this only controls
  // whether the caller names an existing one or lets one be created.
  groupId: z.string().min(1).optional(),
  dayOfWeek: z.number().int().min(1).max(7),
  startTime: hhmm,
  durationMin: z.number().int().positive().max(600),
  maxCapacity: z.number().int().positive().max(100).nullable().optional(),
  name: z.string().max(120).nullable().optional(),
  // Optional and nullable, and the two mean different things on update
  // (INVARIANTS §4): omitted = leave the slot's branch alone, null = detach it,
  // a string = assign. Validated against the org (and rejected when archived)
  // in the route — a schema cannot know the tenant.
  branchId: z.string().min(1).nullable().optional(),
  teachers: z.array(slotTeacherInputSchema).max(10).optional(),
});

// THI-267 fix wave (C1): `groupId` is NOT a field of this schema, on purpose
// — reassigning an existing khung giờ to a different class is not a feature
// (routes/classes.ts's own PUT is the only door that moves slots between
// groups, and it never lets the caller name an arbitrary groupId either).
// `.strict()` turns a caller that still sends one into a 400 instead of the
// field being silently dropped — the closest thing to "the API says so" a
// zod schema can do. A caller-supplied `groupId` on `createSlotSchema` above
// is legitimate (an explicit "put this new khung giờ in this class") and
// stays; only the update path had no validation on it at all (branch-review
// C1: it went straight from the wire into `classSlot.update`, unscoped).
export const updateSlotSchema = z.object({
  dayOfWeek: z.number().int().min(1).max(7).optional(),
  startTime: hhmm.optional(),
  durationMin: z.number().int().positive().max(600).optional(),
  // No `maxCapacity`: sĩ số belongs to the class (spec §3, written through to
  // every khung giờ by `PUT /api/classes/:id`); a per-khung-giờ edit would
  // let the grid card and the class row disagree.
  name: z.string().max(120).nullable().optional(),
  // Optional and nullable, and the two mean different things on update
  // (INVARIANTS §4): omitted = leave the slot's branch alone, null = detach it,
  // a string = assign. Validated against the org (and rejected when archived)
  // in the route — a schema cannot know the tenant.
  branchId: z.string().min(1).nullable().optional(),
  teachers: z.array(slotTeacherInputSchema).max(10).optional(),
}).strict();

// ── THI-267: the class layer (ClassGroup / "lớp") ───────────────────────────

/** One khung giờ row of `createClassSchema`/`updateClassSchema`'s `slots`
 *  array. `id` is meaningless on create (a class has no existing rows yet)
 *  and is what `updateClassSchema` uses to tell "edit this row" (id present,
 *  routed through the existing PUT-slot semantics) from "create a new one"
 *  (id absent) — a row present on the class but missing from the update's
 *  list is archived (spec §4). */
export const classSlotRowSchema = z.object({
  id: z.string().min(1).optional(),
  dayOfWeek: z.number().int().min(1).max(7),
  startTime: hhmm,
  durationMin: z.number().int().min(15).max(600),
  teacherId: z.string().min(1).optional(),
  branchId: z.string().min(1).nullable().optional(),
});

/** The `defaults…` object of `createClassSchema`/`updateClassSchema` —
 *  mirrors `ClassGroup`'s `default*` columns one for one. Every field is
 *  `nullable().optional()` even on create (INVARIANTS §4.1 discipline
 *  applied uniformly): omitted = not mentioned, `null` = explicitly unset
 *  (no term/no charge-absent policy/etc), a value = set it. */
export const classDefaultsSchema = z.object({
  pricingType: z.enum(['PER_SESSION', 'MONTHLY_FLAT']).nullable().optional(),
  billingPeriod: z.enum(['MONTHLY', 'TERM']).nullable().optional(),
  termSize: z.number().int().positive().nullable().optional(),
  // Fix round 1 (Minor-5): `.positive()`, not `.min(0)` — a 0-value class
  // default is not a price a row should silently inherit (it would turn
  // every rate-less import row into a 0 ₫ enrollment instead of the
  // RATE_REQUIRED error the teacher needs to see). `addClassMembers` treats
  // a stored default of 0 the same as absent, for the same reason.
  ratePerSession: z.number().positive().nullable().optional(),
  monthlyRate: z.number().positive().nullable().optional(),
  chargeAbsent: z.boolean().nullable().optional(),
});

export const createClassSchema = z.object({
  subjectId: z.string().min(1),
  name: z.string().trim().max(120).nullable().optional(),
  room: z.string().trim().max(120).nullable().optional(),
  maxCapacity: z.number().int().positive().max(100).nullable().optional(),
  // Same date-string shape as joinSlotSchema.startDate below: an ISO
  // date/datetime, required here (a class always has a start).
  startsOn: z.string().min(1),
  endsOn: z.string().min(1).nullable().optional(),
  color: z.enum(CLASS_COLORS).nullable().optional(),
  defaults: classDefaultsSchema.optional(),
  slots: z.array(classSlotRowSchema).min(1).max(7),
});

/** Longhand, not `.partial()` of create (INVARIANTS §4.1): `subjectId` is
 *  absent on purpose (a class's subject cannot change — same rule as a
 *  slot's, THI-262), and every other field keeps create's own nullability
 *  rather than inheriting whatever `.partial()` would do to it. `startsOn`
 *  stays non-nullable (the column is NOT NULL); everything else that can be
 *  cleared on `ClassGroup` is `nullable().optional()` here too. */
export const updateClassSchema = z.object({
  name: z.string().trim().max(120).nullable().optional(),
  room: z.string().trim().max(120).nullable().optional(),
  maxCapacity: z.number().int().positive().max(100).nullable().optional(),
  startsOn: z.string().min(1).optional(),
  endsOn: z.string().min(1).nullable().optional(),
  color: z.enum(CLASS_COLORS).nullable().optional(),
  defaults: classDefaultsSchema.optional(),
  slots: z.array(classSlotRowSchema).min(1).max(7).optional(),
});

// Batch add (`POST /api/classes/:id/members`). A row either points at an
// existing student or asks one to be created inline — same two shapes as
// the rest of the app's "quick add a student" surfaces, but the payer here
// additionally allows the bare literal 'SELF' (the "Học viên tự trả" case),
// which the existing `payerSchema` in ./student.ts does not model.
const classMemberPayerSchema = z.union([
  z.object({ clientId: z.string().min(1) }),
  z.object({
    name: z.string().min(1).max(100),
    phone: z.string().nullable().optional(),
    email: z.string().email().nullable().optional(),
  }),
  z.literal('SELF'),
]);

const classNewStudentSchema = z.object({
  name: z.string().min(1).max(100),
  nickname: z.string().max(100).nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  payer: classMemberPayerSchema,
});

// Fields common to both row shapes below. `rate` is the per-row price
// (interpretation — per-session or monthly — follows the batch's own
// `pricing.pricingType`); `slotIds` is a subset of the class's khung giờ,
// defaulting to all of them when omitted; `existingEnrollmentAction` only
// matters when the student already has a live enrollment in the class's
// subject.
const classMemberRowBase = {
  // Fix round 1 (Minor-11 → Task 7 fix round 1, Ruling 1): optional, not
  // required — `addClassMembers` falls back to the class's own default rate,
  // then the subject's, before rejecting with `RATE_REQUIRED`. `.positive()`
  // still holds when a rate IS given, matching `createEnrollmentSchema` — a
  // zero-rate row would create an enrollment `POST /api/enrollments` itself
  // would reject, billing 0 ₫ silently.
  rate: z.number().positive().optional(),
  slotIds: z.array(z.string().min(1)).optional(),
  existingEnrollmentAction: z.enum(['KEEP', 'REPRICE']).optional(),
};

const addClassMemberRowSchema = z.union([
  z.object({ studentId: z.string().min(1), ...classMemberRowBase }),
  z.object({ newStudent: classNewStudentSchema, ...classMemberRowBase }),
]).refine(
  // Task 7 fix round 2 — `rate` became optional (fix round 1, Ruling 1) so a
  // brand-new/KEEP row can fall back to the class/subject default. That
  // fallback must NOT reach a REPRICE row: omitting `rate` there used to be
  // a Zod-level impossibility, and without this guard it silently reprices
  // the student to whatever the class/subject default happens to be, with
  // no explicit new price ever supplied by the caller.
  (row) => row.existingEnrollmentAction !== 'REPRICE' || row.rate !== undefined,
  { message: 'Đổi học phí thì phải điền học phí mới.', path: ['rate'] },
);

export const addClassMembersSchema = z.object({
  // Same date-string shape as joinSlotSchema.startDate below.
  startDate: z.string().min(1),
  pricing: z.object({
    pricingType: z.enum(['PER_SESSION', 'MONTHLY_FLAT']),
    billingPeriod: z.enum(['MONTHLY', 'TERM']).optional(),
    termSize: z.number().int().positive().optional(),
    chargeAbsent: z.boolean().optional(),
  }).refine(
    // Fix round 1 (Important-5), fix wave (Task 5 NEW-5): matches
    // createEnrollmentSchema's own refine (student.ts) exactly, including the
    // `pricingType !== 'PER_SESSION'` escape — a MONTHLY_FLAT+TERM row is not
    // this refine's business either, same as createEnrollmentSchema's. This
    // only catches a batch that names TERM directly; the class-default
    // cascade (a class with no defaultTermSize) is caught separately, at
    // write time, by createEnrollment's own re-check.
    (v) => v.pricingType !== 'PER_SESSION' || v.billingPeriod !== 'TERM' || (v.termSize != null && v.termSize > 0),
    { message: 'termSize bắt buộc khi billingPeriod = TERM', path: ['termSize'] },
  ),
  rows: z.array(addClassMemberRowSchema).min(1).max(100),
});

// PUT /api/classes/:id/members/:enrollmentId — replace the member's live
// khung giờ set with `slotIds` (may be empty, meaning "leave every slot of
// this class"); the route diffs against current live memberships to decide
// joins vs leaves. `joinedAt` only matters for slots newly joined by the
// diff — same §1.15 companion rule as everywhere else a join date is taken.
export const updateClassMemberSlotsSchema = z.object({
  slotIds: z.array(z.string().min(1)),
  joinedAt: z.string().min(1).optional(),
});

export type ClassSlotRowInput = z.infer<typeof classSlotRowSchema>;
export type ClassDefaultsInput = z.infer<typeof classDefaultsSchema>;
export type CreateClassInput = z.infer<typeof createClassSchema>;
export type UpdateClassInput = z.infer<typeof updateClassSchema>;
export type AddClassMembersInput = z.infer<typeof addClassMembersSchema>;
export type UpdateClassMemberSlotsInput = z.infer<typeof updateClassMemberSlotsSchema>;

export const joinSlotSchema = z.object({
  enrollmentId: z.string().min(1),
  // ISO date/datetime — when the member's start boundary (joinedAt) should take
  // effect. Optional; server defaults to today (nowWallClock) when omitted.
  startDate: z.string().min(1).optional(),
});

// Bulk reassignment: move a batch of students from one slot to another in a
// single transaction, effective immediately (no startDate — see the design
// doc's "effective immediately, by decision"). Capped at 200: generous over
// the ~80-student twice-a-year reshuffle this exists for, without leaving the
// batch unbounded.
export const moveSlotMembersSchema = z.object({
  fromSlotId: z.string().min(1),
  toSlotId: z.string().min(1),
  enrollmentIds: z.array(z.string().min(1)).min(1).max(200),
});

/** A branch name. Trimmed before length is measured, so "   " is rejected as
 *  empty rather than stored as three spaces. 100 chars is generous for
 *  "Cơ sở Nguyễn Văn Cừ (tầng 3)" and short enough to render in the header
 *  picker without truncation games. */
export const branchNameSchema = z.string().trim().min(1, 'Tên chi nhánh không được để trống').max(100, 'Tên chi nhánh tối đa 100 ký tự');

/** A `?branchId=` filter value: a branch cuid, or the literal `none` meaning
 *  "Chưa gán chi nhánh" — slots that exist but carry no branch.
 *
 *  The literal is written out here rather than imported from
 *  `@repo/shared-types` (`UNASSIGNED_BRANCH`) because this package deliberately
 *  does not depend on that one — same mirroring, and same reason, as the note in
 *  `invoice.ts:90`. If you change one, change the other; `branchFilter.ts`
 *  re-exports the shared-types constant so the API only ever sees one name.
 *
 *  cuid, not `min(1)`: it is what makes `none` unambiguous. Prisma ids are
 *  `c…`, so no branch can ever be called `none`. */
export const branchFilterSchema = z.union([z.literal('none'), z.string().cuid()]);

/** `assignAllUnassigned` is the adopt moment (THI-145): when a Center org creates
 *  its FIRST branch, the dialog offers — default ON — to move every class that
 *  has no branch into it, so the feature starts useful instead of starting with
 *  an empty cơ sở beside seven orphans. Optional and absent-means-false: every
 *  existing caller keeps working, and the second-and-later create dialog does not
 *  send it at all. */
export const createBranchSchema = z.object({
  name: branchNameSchema,
  assignAllUnassigned: z.boolean().optional(),
});
/** Longhand, not a `.partial()` of create (INVARIANTS §4): rename is the only
 *  editable field and it is required — an omitted name here would mean
 *  "clear it", which is not a thing a branch can be. */
export const updateBranchSchema = z.object({ name: branchNameSchema });

export type CreateBranchInput = z.infer<typeof createBranchSchema>;
export type UpdateBranchInput = z.infer<typeof updateBranchSchema>;

export const createSessionSchema = z.object({
  subjectId: z.string().min(1),
  startAt: z.string().min(1),
  durationMin: z.number().int().positive().max(600),
  enrollmentIds: z.array(z.string().min(1)).min(1),
  note: z.string().nullable().optional(),
  // GV chịu trách nhiệm for the new session. Defaults to the caller's own
  // membership server-side when omitted — matching createSlotSchema's
  // teacherId default, so a one-off session's teacher-picker can share the
  // same fallback logic as the slot form.
  teacherId: z.string().min(1).optional(),
  // Optional additional teachers, same shape as a slot's list (no rate —
  // no session-level pay override, by design).
  teachers: z.array(sessionTeacherInputSchema).max(10).optional(),
});

export const updateSessionSchema = z.object({
  startAt: z.string().min(1).optional(),
  durationMin: z.number().int().positive().max(600).optional(),
  teacherId: z.string().min(1).optional(),
  note: z.string().nullable().optional(),
  teachers: z.array(sessionTeacherInputSchema).max(10).optional(),
});

export const addParticipantSchema = z.object({ enrollmentId: z.string().min(1) });
export const moveParticipantSchema = z.object({
  enrollmentId: z.string().min(1),
  toSessionId: z.string().min(1),
});

export const markAttendanceSchema = z.object({
  marks: z
    .array(
      z.object({
        enrollmentId: z.string().min(1),
        // PENDING = un-mark (reset an accidental/changed attendance back to not-yet-marked).
        attendance: z.enum(['PRESENT', 'ABSENT', 'EXCUSED', 'PENDING']),
        note: z.string().max(500).nullable().optional(),
      }),
    )
    .min(1),
});

export type CreateSlotInput = z.infer<typeof createSlotSchema>;
export type UpdateSlotInput = z.infer<typeof updateSlotSchema>;
export type SlotTeacherInput = z.infer<typeof slotTeacherInputSchema>;
export type SessionTeacherInput = z.infer<typeof sessionTeacherInputSchema>;
export type JoinSlotInput = z.infer<typeof joinSlotSchema>;
export type MoveSlotMembersInput = z.infer<typeof moveSlotMembersSchema>;
export type CreateSessionInput = z.infer<typeof createSessionSchema>;
export type UpdateSessionInput = z.infer<typeof updateSessionSchema>;
export type AddParticipantInput = z.infer<typeof addParticipantSchema>;
export type MoveParticipantInput = z.infer<typeof moveParticipantSchema>;
export type MarkAttendanceInput = z.infer<typeof markAttendanceSchema>;

// ── Mẫu lịch học (schedule templates) ──
// A template is a named list of (slot × học viên) pairs and nothing else.
const templateName = z.string().trim().min(1, 'Tên mẫu không được để trống').max(120);

export const createScheduleTemplateSchema = z.object({
  name: templateName,
  // true = snapshot every live SlotEnrollment of every ACTIVE slot into
  // entries; false = start empty.
  copyFromCurrent: z.boolean(),
});

export const renameScheduleTemplateSchema = z.object({ name: templateName });

// Whole-state save: the draft editor PUTs the full entry set, so there are
// no per-op endpoints to keep consistent with each other. 2000 is ~10x the
// biggest real timetable and keeps one request (and one transaction)
// bounded.
export const putScheduleTemplateEntriesSchema = z.object({
  entries: z
    .array(z.object({ slotId: z.string().min(1), enrollmentId: z.string().min(1) }))
    .max(2000, 'Mẫu vượt quá 2000 dòng'),
});

// POST /:id/apply — optional body. `backupNamePrefix` names the auto-backup
// template apply snapshots the live timetable into before executing the diff
// (see scheduleTemplates.ts); omitted, the route defaults it to "Backup".
export const applyScheduleTemplateSchema = z.object({
  backupNamePrefix: z.string().trim().min(1).max(30).optional(),
});

export type CreateScheduleTemplateInput = z.infer<typeof createScheduleTemplateSchema>;
export type RenameScheduleTemplateInput = z.infer<typeof renameScheduleTemplateSchema>;
export type PutScheduleTemplateEntriesInput = z.infer<typeof putScheduleTemplateEntriesSchema>;
export type ApplyScheduleTemplateInput = z.infer<typeof applyScheduleTemplateSchema>;
